import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { supabase } from '@/integrations/supabase/client';
import { setSentryUser } from '@/lib/sentry-defer';


interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Prevent double initialization in React strict mode
    if (initializedRef.current) return;
    initializedRef.current = true;

    let isMounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      const newUserId = session?.user?.id ?? null;
      // On any actual user change (login, switch, logout), purge cached queries
      // belonging to the previous user so we never flash someone else's data.
      if (previousUserIdRef.current !== newUserId) {
        if (previousUserIdRef.current !== null) {
          queryClient.clear();
        } else if (newUserId !== null) {
          // First time we resolve a user (signup / initial session). Any
          // pre-auth queries should refetch with the now-available JWT instead
          // of holding an empty pre-auth snapshot.
          queryClient.invalidateQueries({ queryKey: ['workout-routines'] });
          queryClient.invalidateQueries({ queryKey: ['active-program'] });
        }
        previousUserIdRef.current = newUserId;
      }

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // THEN check for existing session with timeout safeguard
    const timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        setLoading(false);
      }
    }, 5000);

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!isMounted) return;
      clearTimeout(timeoutId);
      
      if (error) {
        console.error('[Auth] getSession error:', error);
      }
      
      previousUserIdRef.current = session?.user?.id ?? null;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch((err) => {
      if (!isMounted) return;
      clearTimeout(timeoutId);
      console.error('[Auth] getSession error:', err);
      setLoading(false);
    });

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Analytics identity ────────────────────────────────────────────────
  // Keyed on the user id only, so it fires on real transitions (login,
  // account switch, logout) and never on re-render. PostHog is identified
  // with the SUPABASE user id so client events join server-side events
  // (edge functions send distinct_id = userId). Person properties are kept
  // minimal — account email only, never health data.
  const identifiedIdRef = useRef<string | null>(null);
  useEffect(() => {
    const uid = user?.id ?? null;
    if (identifiedIdRef.current === uid) return;
    const previousId = identifiedIdRef.current;
    identifiedIdRef.current = uid;

    try {
      if (uid) {
        posthog?.identify(uid, {
          $email: user?.email ?? undefined,
          created_at: user?.created_at ?? undefined,
        });
        setSentryUser({ id: uid, email: user?.email ?? undefined });
      } else if (previousId !== null) {
        // Only reset on a real sign-out, not on the initial anonymous state,
        // so we don't discard the pre-auth anonymous id before signup.
        posthog?.reset();
        setSentryUser(null);
      }
    } catch {
      // Telemetry must never break auth.
    }
  }, [user?.id, user?.email, user?.created_at]);

  const signOut = useCallback(async () => {

    // Clear coach/check-in localStorage flags so a different user signing in
    // on the same device is not affected by the previous user's state.
    try {
      localStorage.removeItem('last_coach_checkin_week');
      localStorage.removeItem('dismissedWeeklyCheckin');
      localStorage.removeItem('dismissedMonthlyReport');
    } catch {
      // localStorage may be unavailable (private mode, SSR); non-fatal.
    }
    await supabase.auth.signOut();
    // Clear all cached queries so the next signed-in user can't see prior data.
    queryClient.clear();
    previousUserIdRef.current = null;
    setUser(null);
    setSession(null);
  }, [queryClient]);

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
